import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Building2, User, Kanban, Package, FileText, FileSearch, Paperclip, Activity, LayoutGrid, BarChart3, Plus, Sparkles, Upload, CornerDownLeft } from "lucide-react";
import { CommandDialog, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, CommandSeparator } from "@/components/ui/command";
import { api } from "@/lib/api";

const TYPE_ICON = { account: Building2, contact: User, opportunity: Kanban, product: Package, quote: FileText, tender: FileSearch, document: Paperclip, activity: Activity };
const TYPE_LABEL = { account: "Customers", contact: "Contacts", opportunity: "Opportunities", product: "Products", quote: "Quotes", tender: "Tenders", document: "Documents", activity: "Activity" };

const NAV = [
  { to: "/overview", label: "Overview", icon: LayoutGrid },
  { to: "/pipeline", label: "Opportunities", icon: Kanban },
  { to: "/quotes", label: "Quotes", icon: FileText },
  { to: "/tenders", label: "Tenders", icon: FileSearch },
  { to: "/customers", label: "Customers", icon: Building2 },
  { to: "/products", label: "Products", icon: Package },
  { to: "/insights", label: "Insights", icon: BarChart3 },
];

function IconTile({ icon: Icon, accent }) {
  return (
    <span className={accent ? "flex h-7 w-7 items-center justify-center rounded-lg bg-[hsl(10_76%_61%/0.1)] text-primary" : "flex h-7 w-7 items-center justify-center rounded-lg bg-black/[0.05] text-muted-foreground"}>
      <Icon className="h-3.5 w-3.5" />
    </span>
  );
}

export function CommandPalette({ open, onOpenChange, onNewInquiry, onOpenAI }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) {
      setQ("");
      setResults([]);
    }
  }, [open]);

  useEffect(() => {
    if (!q.trim()) {
      setResults([]);
      return undefined;
    }
    setLoading(true);
    const t = setTimeout(() => {
      api
        .get("/search", { params: { q } })
        .then((r) => setResults(r.data.results || []))
        .catch(() => setResults([]))
        .finally(() => setLoading(false));
    }, 180);
    return () => clearTimeout(t);
  }, [q]);

  const grouped = useMemo(() => {
    const g = {};
    results.forEach((r) => {
      (g[r.type] = g[r.type] || []).push(r);
    });
    return g;
  }, [results]);

  const go = (path) => {
    onOpenChange(false);
    navigate(path);
  };

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange}>
      <CommandInput placeholder="Search or jump to…" value={q} onValueChange={setQ} data-testid="command-palette-input" />
      <CommandList className="max-h-[440px]">
        <CommandEmpty>{loading ? "Searching…" : q ? "No matches. Try a customer, product or quote number." : "Type to search across everything."}</CommandEmpty>
        {!q && (
          <>
            <CommandGroup heading="Create">
              <CommandItem onSelect={() => { onOpenChange(false); onNewInquiry(); }} data-testid="command-new-inquiry">
                <IconTile icon={Plus} accent /> <span className="flex-1">New inquiry</span><span className="text-xs text-muted-foreground">paste customer text</span>
              </CommandItem>
              <CommandItem onSelect={() => go("/tenders?new=1")} data-testid="command-new-tender">
                <IconTile icon={Upload} /> <span className="flex-1">New tender</span><span className="text-xs text-muted-foreground">upload documents</span>
              </CommandItem>
              <CommandItem onSelect={() => { onOpenChange(false); onOpenAI(); }} data-testid="command-ask-ai">
                <IconTile icon={Sparkles} accent /> <span className="flex-1">Ask the AI assistant</span><span className="kbd">⌘J</span>
              </CommandItem>
            </CommandGroup>
            <CommandSeparator />
            <CommandGroup heading="Navigate">
              {NAV.map((n) => (
                <CommandItem key={n.to} onSelect={() => go(n.to)} data-testid={`command-nav-${n.label.toLowerCase()}`}>
                  <IconTile icon={n.icon} /> <span className="flex-1">{n.label}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}
        {Object.entries(grouped).map(([type, items]) => {
          const Icon = TYPE_ICON[type] || Activity;
          return (
            <CommandGroup key={type} heading={TYPE_LABEL[type] || type}>
              {items.map((r) => (
                <CommandItem key={`${type}-${r.id}`} value={`${type}-${r.id}-${r.title}`} onSelect={() => go(r.link)} data-testid={`search-result-${type}`}>
                  <IconTile icon={Icon} />
                  <span className="flex-1 truncate">{r.title}</span>
                  <span className="ml-3 truncate text-xs text-muted-foreground">{r.subtitle}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          );
        })}
      </CommandList>
      <div className="flex items-center justify-between border-t border-black/[0.06] bg-[hsl(40_12%_98%)] px-4 py-2 text-[11.5px] text-muted-foreground">
        <span className="flex items-center gap-3"><span className="flex items-center gap-1"><span className="kbd">↑</span><span className="kbd">↓</span> navigate</span><span className="flex items-center gap-1"><span className="kbd"><CornerDownLeft className="h-3 w-3" /></span> open</span></span>
        <span className="flex items-center gap-1"><span className="kbd">esc</span> close</span>
      </div>
    </CommandDialog>
  );
}
